import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { switchMap } from 'rxjs';
import { PortCallModel } from '../../../../shared/models/port-call.model';
import { PortCallPdaModel } from '../../../../shared/models/port-call-pda.model';
import { PortCallPdaLineModel } from '../../../../shared/models/port-call-pda-line.model';
import { PortCallPdaService } from '../../../../shared/services/port-call-pda.service';
import { PortTariffLineTypes } from '../../../../shared/enums/port-tariff-line-types.enum';
import { PortTariffLineUnitTypes } from '../../../../shared/enums/port-tariff-line-unit-types.enum';
import { PortTariffDisplayService } from '../../../../shared/services/port-tariff-display.service';
import { PortTariffLineVesselProperties } from '../../../../shared/enums/port-tariff-line-vessel-properties.enum';
import { PortTariffLineExpenseCategories } from '../../../../shared/enums/port-tariff-line-expense-category.enum';
import { PortTariffLineOperationDirections } from '../../../../shared/enums/port-tariff-line-operation-direction.enum';
import { PortCallPdaStatuses } from '../../../../shared/enums/port-call-pda-status.enum';
import { PortCallPdaLineStatuses } from '../../../../shared/enums/port-call-pda-line-status.enum';
import { TagContext } from '../../../../shared/enums/tag-context.enum';
import { PortOvertimeLevel } from '../../../../shared/enums/port-overtime-level.enum';
import { AlertService, AlertType, LoaderComponent, LoaderService } from '@solcre-org/core-ui';

// One aggregated row per overtime level (regular/ot1/ot2) for a PDA line's
// breakdown table — summed across however many raw `overtimeApplications`
// segments the API returned for that level.
interface PortCallPdaOvertimeLevelSummary {
  level: PortOvertimeLevel | string;
  hours: number;
  rateValue: string | null;
  amountUsd: number;
  rateApplied: boolean;
  fallbackCode: string | null;
}

@Component({
  selector: 'app-port-call-pda',
  imports: [CommonModule, TranslateModule, LoaderComponent],
  templateUrl: './port-call-pda.html',
  styleUrls: ['./port-call-pda.css'],
})
export class PortCallPdaModal {
  // Inputs / Outputs
  portCall = input<PortCallModel | null>(null);
  isOpen = input<boolean>(false);
  closed = output<void>();
  pdaUpdated = output<void>();

  // Services
  private pdaService = inject(PortCallPdaService);
  private loaderService = inject(LoaderService);
  private alertService = inject(AlertService);
  private displayService = inject(PortTariffDisplayService);

  readonly PdaLineStatus = PortCallPdaLineStatuses;
  readonly PortOvertimeLevel = PortOvertimeLevel;
  // Backend cargo-matching outcome for a cargo-basis line: 'not_found' means the
  // formula ran fine but no booking matched the line's cargo filter — a 0.00 amount
  // in that case is a valid result, not a sign of missing data.
  readonly CARGO_MATCH_NOT_FOUND = 'not_found';
  readonly categoryOrder = [
    PortTariffLineExpenseCategories.PORT,
    PortTariffLineExpenseCategories.STEVEDORING,
    PortTariffLineExpenseCategories.AGENCY,
  ];
  readonly overtimeLevelOrder = [
    PortOvertimeLevel.REGULAR,
    PortOvertimeLevel.OT1,
    PortOvertimeLevel.OT2,
  ];

  // State
  pda = signal<PortCallPdaModel | null>(null);
  notFound = signal<boolean>(false);
  error = signal<string | null>(null);
  editingLineId = signal<number | null>(null);
  overrideDraft = signal<{ manualAmountUsd: string; overrideReason: string }>({
    manualAmountUsd: '',
    overrideReason: '',
  });
  showRecalculateConfirm = signal<boolean>(false);
  syncTariffOnRecalculate = signal<boolean>(false);
  addingManualItemFor = signal<string | null>(null);
  manualItemDraft = signal<{ concept: string; rateValue: string }>({
    concept: '',
    rateValue: '',
  });
  expandedOvertimeLineIds = signal<Set<any>>(new Set());

  // Computed
  allLines = computed(() =>
    (this.pda()?.lines ?? [])
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  );

  lineGroups = computed(() =>
    this.categoryOrder.map((category) => {
      const lines = this.allLines().filter(
        (line) => (line.expenseCategory ?? PortTariffLineExpenseCategories.PORT) === category
      );
      const subtotal = lines.reduce((sum, line) => sum + Number(line.effectiveAmountUsd ?? 0), 0);
      return { category, lines, subtotal };
    })
  );

  isApproved = computed(() => this.pda()?.status === PortCallPdaStatuses.APPROVED);

  hasOutdatedInputs = computed(() => {
    const currentPda = this.pda();

    if (!currentPda?.hasStaleInputs) {
      return false;
    }

    // A manual override is intentionally stale metadata, but it does not mean
    // that the calculated PDA inputs are outdated. The dedicated override
    // notice below communicates that state separately.
    return currentPda.staleReasons.length === 0
      || currentPda.staleReasons.some((reason) => reason !== 'manual_overrides_present');
  });

  portCallTitle = computed(() => {
    const pc = this.portCall();
    return pc?.portDetails?.fullName ?? pc?.portDetails?.portCode ?? '';
  });

  constructor() {
    effect(() => {
      const pc = this.portCall();
      const open = this.isOpen();
      if (open && pc?.id) {
        this.loadPda(pc.id);
      }
      if (!open) {
        this.reset();
      }
    });
  }

  private loadPda(portCallId: any): void {
    this.notFound.set(false);
    this.error.set(null);
    this.loaderService.showLoader();

    this.pdaService.fetchPda(portCallId).subscribe({
      next: (pda) => {
        this.loaderService.hideLoader();
        this.pda.set(pda);
      },
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        if (err.status === 404) {
          this.pda.set(null);
          this.notFound.set(true);
        } else {
          this.error.set(err.error?.detail ?? 'errors.generic');
        }
      },
    });
  }

  onCreatePda(): void {
    const portCallId = this.portCall()?.id;
    if (!portCallId) return;

    this.loaderService.showLoader();
    this.pdaService.createPda(portCallId).subscribe({
      next: (pda) => {
        this.loaderService.hideLoader();
        this.pda.set(pda);
        this.notFound.set(false);
        this.pdaUpdated.emit();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: 'pda.created',
          duration: 2000,
          inBottom: true,
        });
      },
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: err.error?.error ?? err.error?.detail ?? 'errors.generic',
          duration: 3000,
          inBottom: true,
        });
      },
    });
  }

  openRecalculateConfirm(): void {
    this.syncTariffOnRecalculate.set(false);
    this.showRecalculateConfirm.set(true);
  }

  closeRecalculateConfirm(): void {
    this.showRecalculateConfirm.set(false);
  }

  confirmRecalculate(): void {
    const refreshTariffLines = this.syncTariffOnRecalculate();
    this.showRecalculateConfirm.set(false);
    this.onRecalculate(refreshTariffLines);
  }

  onRecalculate(refreshTariffLines = false): void {
    const pdaId = this.pda()?.id;
    const portCallId = this.portCall()?.id;
    if (!pdaId) return;

    this.loaderService.showLoader();
    this.pdaService.recalculate(pdaId, refreshTariffLines).pipe(
      switchMap((pda) => (refreshTariffLines && portCallId ? this.pdaService.fetchPda(portCallId) : [pda]))
    ).subscribe({
      next: (pda) => {
        this.loaderService.hideLoader();
        this.pda.set(pda);
        this.pdaUpdated.emit();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: 'pda.recalculated',
          duration: 2000,
          inBottom: true,
        });
      },
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: err.error?.detail ?? 'errors.generic',
          duration: 3000,
          inBottom: true,
        });
      },
    });
  }

  onApprove(): void {
    const pdaId = this.pda()?.id;
    if (!pdaId || this.isApproved()) return;

    this.loaderService.showLoader();
    this.pdaService.approve(pdaId).subscribe({
      next: (pda) => {
        this.loaderService.hideLoader();
        this.pda.set(pda);
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: 'pda.approved',
          duration: 2000,
          inBottom: true,
        });
      },
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: err.error?.detail ?? 'errors.generic',
          duration: 3000,
          inBottom: true,
        });
      },
    });
  }

  onEditLine(line: PortCallPdaLineModel): void {
    this.editingLineId.set(line.id);
    this.overrideDraft.set({
      manualAmountUsd: line.manualAmountUsd ?? line.effectiveAmountUsd ?? '',
      overrideReason: line.overrideReason ?? '',
    });
  }

  onCancelEdit(): void {
    this.editingLineId.set(null);
    this.overrideDraft.set({ manualAmountUsd: '', overrideReason: '' });
  }

  onSaveOverride(lineId: any): void {
    const draft = this.overrideDraft();
    const amount = draft.manualAmountUsd.trim();

    if (!amount || isNaN(Number(amount)) || Number(amount) < 0) {
      this.alertService.show({
        type: AlertType.ERROR,
        message: 'pda.invalidAmount',
        duration: 3000,
        inBottom: true,
      });
      return;
    }

    this.loaderService.showLoader();
    this.pdaService
      .overrideLine(lineId, {
        manualAmountUsd: Number(amount).toFixed(2),
        overrideReason: draft.overrideReason || undefined,
      })
      .subscribe({
        next: (pda) => {
          this.loaderService.hideLoader();
          this.pda.set(pda);
          this.editingLineId.set(null);
          this.pdaUpdated.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.loaderService.hideLoader();
          this.alertService.show({
            type: AlertType.ERROR,
            message: err.error?.error ?? err.error?.detail ?? 'errors.generic',
            duration: 3000,
            inBottom: true,
          });
        },
      });
  }

  onResetLine(lineId: any): void {
    this.loaderService.showLoader();
    this.pdaService.resetLine(lineId).subscribe({
      next: (pda) => {
        this.loaderService.hideLoader();
        this.pda.set(pda);
        this.pdaUpdated.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: err.error?.detail ?? 'errors.generic',
          duration: 3000,
          inBottom: true,
        });
      },
    });
  }

  onClose(): void {
    this.closed.emit();
  }

  updateOverrideDraft(field: 'manualAmountUsd' | 'overrideReason', value: string): void {
    this.overrideDraft.update((d) => ({ ...d, [field]: value }));
  }

  onAddManualItem(category: string): void {
    this.addingManualItemFor.set(category);
    this.manualItemDraft.set({ concept: '', rateValue: '' });
  }

  onCancelManualItem(): void {
    this.addingManualItemFor.set(null);
    this.manualItemDraft.set({ concept: '', rateValue: '' });
  }

  onSaveManualItem(): void {
    const pdaId = this.pda()?.id;
    const category = this.addingManualItemFor();
    if (!pdaId || !category) return;

    const draft = this.manualItemDraft();
    const concept = draft.concept.trim();
    const rate = Number(draft.rateValue);
    const decimals = draft.rateValue.includes('.') ? draft.rateValue.split('.')[1].length : 0;

    if (!concept || !draft.rateValue.trim() || isNaN(rate) || rate < 0 || decimals > 4) {
      this.alertService.show({
        type: AlertType.ERROR,
        message: 'pda.invalidAmount',
        duration: 3000,
        inBottom: true,
      });
      return;
    }

    this.loaderService.showLoader();
    this.pdaService
      .addManualLine(pdaId, {
        concept,
        lineType: PortTariffLineTypes.STANDARD,
        rateValue: rate.toFixed(4),
        unitType: PortTariffLineUnitTypes.FIXED,
        vesselProperty: PortTariffLineVesselProperties.NONE,
        expenseCategory: category,
        operationDirection: PortTariffLineOperationDirections.BOTH,
      })
      .subscribe({
        next: (pda) => {
          this.loaderService.hideLoader();
          this.pda.set(pda);
          this.pdaUpdated.emit();
          this.onCancelManualItem();
          this.alertService.show({
            type: AlertType.SUCCESS,
            message: 'pda.manualItem.added',
            duration: 2000,
            inBottom: true,
          });
        },
        error: (err: HttpErrorResponse) => {
          this.loaderService.hideLoader();
          this.alertService.show({
            type: AlertType.ERROR,
            message: err.error?.error ?? err.error?.detail ?? 'errors.generic',
            duration: 3000,
            inBottom: true,
          });
        },
      });
  }

  updateManualItemDraft(field: 'concept' | 'rateValue', value: string): void {
    this.manualItemDraft.update((d) => ({ ...d, [field]: value }));
  }

  cargoOperationSummary = computed(() => {
    const summary = this.pda()?.cargoOperationSummary;
    return (summary?.loadingQuantity ?? 0) + (summary?.dischargeQuantity ?? 0);
  });

  lineHasOvertimeBreakdown(line: PortCallPdaLineModel): boolean {
    return (line.overtimeBreakdown ?? []).length > 0;
  }

  // The API returns one `overtimeApplications` entry per time segment the proration
  // engine walked through — a line can have many "regular" segments plus a handful
  // of ot1/ot2 ones. The table shows one row per level, so segments are summed here.
  lineOvertimeEntries(line: PortCallPdaLineModel): PortCallPdaOvertimeLevelSummary[] {
    return this.aggregateOvertimeByLevel(line);
  }

  private aggregateOvertimeByLevel(line: PortCallPdaLineModel): PortCallPdaOvertimeLevelSummary[] {
    const byLevel = new Map<string, PortCallPdaOvertimeLevelSummary>();
    for (const application of line.overtimeBreakdown ?? []) {
      const level = (application.level as string) ?? PortOvertimeLevel.REGULAR;
      const hours = Number(application.minutes ?? 0) / 60;
      const amountUsd = Number(application.calculatedAmountUsd ?? 0);
      const rateApplied = application.appliedRateValue !== null && application.appliedRateValue !== undefined;
      const summary = byLevel.get(level);
      if (summary) {
        summary.hours += hours;
        summary.amountUsd += amountUsd;
        summary.rateApplied = summary.rateApplied && rateApplied;
        summary.fallbackCode = summary.fallbackCode ?? application.fallbackCode ?? null;
        summary.rateValue = summary.rateValue ?? application.appliedRateValue ?? null;
      } else {
        byLevel.set(level, {
          level,
          hours,
          amountUsd,
          rateApplied,
          fallbackCode: application.fallbackCode ?? null,
          rateValue: application.appliedRateValue ?? null,
        });
      }
    }
    return [...byLevel.values()].sort(
      (a, b) => this.overtimeLevelOrder.indexOf(a.level as PortOvertimeLevel) - this.overtimeLevelOrder.indexOf(b.level as PortOvertimeLevel)
    );
  }

  lineHasMissingOvertimeRate(line: PortCallPdaLineModel): boolean {
    return this.aggregateOvertimeByLevel(line).some((summary) => !summary.rateApplied);
  }

  // Non-regular levels (OT1/OT2) actually present on this line, in display order —
  // drives the per-level badges so the line shows *which* overtime tier applies
  // instead of a generic "mixed schedule" label.
  lineOvertimeLevels(line: PortCallPdaLineModel): PortOvertimeLevel[] {
    const present = new Set((line.overtimeBreakdown ?? []).map((entry) => entry.level as PortOvertimeLevel));
    return this.overtimeLevelOrder.filter((level) => level !== PortOvertimeLevel.REGULAR && present.has(level));
  }

  // True when one or more OT levels had no complete formula of their own and fell
  // back to the Regular formula instead.
  lineHasFallbackApplied(line: PortCallPdaLineModel): boolean {
    return (line.overtimeBreakdown ?? []).some((entry) => !!entry.fallbackCode);
  }

  // A cargo-basis line (unit/CBM/MT/W-M) carries a cargoMeasureType from the backend
  // once calculated — that's the only reliable signal this line actually priced off
  // a cargo measure. cargoMatchStatus alone isn't enough: non-cargo lines (fixed,
  // vessel property, etc.) can carry it too, and showing the panel just for a bare
  // "not found" status with no measure to explain is an empty accordion.
  lineHasCalculationDetails(line: PortCallPdaLineModel): boolean {
    return !!line.cargoMeasureType;
  }

  // Same expand/collapse toggle drives both the overtime breakdown and the cargo
  // calculation explanation, since a line can have either, both, or neither.
  lineHasExpandableDetails(line: PortCallPdaLineModel): boolean {
    return this.lineHasOvertimeBreakdown(line) || this.lineHasCalculationDetails(line);
  }

  cargoMeasureTypeLabel(measureType: string | undefined): string {
    return this.displayService.unitTypeLabel(measureType);
  }

  cargoMeasureUnit(measureType: string | undefined): string {
    switch (measureType) {
      case PortTariffLineUnitTypes.CARGO_CBM:
        return 'CBM';
      case PortTariffLineUnitTypes.CARGO_MT:
      case PortTariffLineUnitTypes.CARGO_WM:
        return 'MT';
      default:
        return '';
    }
  }

  // W/M prices off whichever of MT/CBM is larger — spelling out the comparison
  // (not just the winning number) is the whole point of this explanation.
  cargoWmFormulaDisplay(line: PortCallPdaLineModel): string {
    const mt = Number(line.cargoWeightMt ?? 0);
    const cbm = Number(line.cargoCubicMeters ?? 0);
    const max = Math.max(mt, cbm);
    return `W/M: max(${this.formatMeasure(mt)} MT, ${this.formatMeasure(cbm)} CBM) = ${this.formatMeasure(max)}`;
  }

  cargoMeasureValueDisplay(line: PortCallPdaLineModel): string {
    if (line.cargoMeasureType === PortTariffLineUnitTypes.CARGO_WM) {
      return this.cargoWmFormulaDisplay(line);
    }

    if (line.cargoMeasureValue === null || line.cargoMeasureValue === undefined) {
      return '—';
    }

    const unit = this.cargoMeasureUnit(line.cargoMeasureType);
    return unit ? `${this.formatMeasure(line.cargoMeasureValue)} ${unit}` : this.formatMeasure(line.cargoMeasureValue);
  }

  formatMeasure(value: string | number | undefined | null): string {
    if (value === null || value === undefined) return '—';
    const num = Number(value);
    return isNaN(num) ? '—' : num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  isOvertimeExpanded(lineId: any): boolean {
    return this.expandedOvertimeLineIds().has(lineId);
  }

  toggleOvertimeBreakdown(lineId: any): void {
    this.expandedOvertimeLineIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  }

  lineStatusContext(status: string | undefined): TagContext {
    switch (status) {
      case PortCallPdaLineStatuses.CALCULATED: return TagContext.SUCCESS;
      case PortCallPdaLineStatuses.MISSING_DATA: return TagContext.WARNING;
      case PortCallPdaLineStatuses.MANUAL: return TagContext.CURRENT;
      default: return TagContext.DEFAULT;
    }
  }

  pdaStatusContext(status: string | undefined): TagContext {
    switch (status) {
      case PortCallPdaStatuses.CALCULATED: return TagContext.SUCCESS;
      case PortCallPdaStatuses.MISSING_DATA: return TagContext.WARNING;
      case PortCallPdaStatuses.APPROVED: return TagContext.CURRENT;
      default: return TagContext.DEFAULT;
    }
  }

  // Same icons/meaning as the port-call activity cards in voyage-routes-portcall.
  operationDirectionIcons(direction: string | undefined): { icon: string; labelKey: string }[] {
    const loading = { icon: 'fa-solid fa-box-arrow-down-arrow-up', labelKey: 'portTariffLines.options.operationDirection.loading' };
    const discharge = { icon: 'fa-solid fa-box-arrow-down', labelKey: 'portTariffLines.options.operationDirection.discharge' };

    switch (direction) {
      case PortTariffLineOperationDirections.LOADING: return [loading];
      case PortTariffLineOperationDirections.DISCHARGE: return [discharge];
      case PortTariffLineOperationDirections.BOTH: return [loading, discharge];
      default: return [];
    }
  }

  formatAmount(value: string | number | undefined | null): string {
    if (value == null) return '$0.00';
    const num = Number(value);
    return isNaN(num) ? '$0.00' : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  formatHours(value: string | number | undefined | null): string {
    if (value == null) return '—';
    const num = Number(value);
    return isNaN(num) ? '—' : `${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}h`;
  }

  private reset(): void {
    this.pda.set(null);
    this.notFound.set(false);
    this.error.set(null);
    this.editingLineId.set(null);
    this.overrideDraft.set({ manualAmountUsd: '', overrideReason: '' });
    this.addingManualItemFor.set(null);
    this.manualItemDraft.set({ concept: '', rateValue: '' });
    this.expandedOvertimeLineIds.set(new Set());
    this.loaderService.hideLoader();
  }
}
